"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { ChevronRight, ChevronLeft } from "lucide-react";
import { getImageUrl } from "@/utils/utils";
import Link from "next/link";

interface HomePageHeroClientProps {
  lang: string;
  sliderData: any[];
  readMoreText?: string;
}

// Smooth slide variants with overlap to prevent white lines
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "101%" : "-101%", // Slightly more than 100% to ensure no gap
    zIndex: 1,
  }),
  center: {
    x: 0,
    zIndex: 2,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-101%" : "101%", // Slightly more than 100% to ensure no gap
    zIndex: 0,
  }),
};

// Smooth content fade variants
const contentVariants = {
  enter: {
    opacity: 0,
    y: 15,
    scale: 0.98,
  },
  center: {
    opacity: 1,
    y: 0,
    scale: 1,
  },
  exit: {
    opacity: 0,
    y: -15,
    scale: 1.02,
  },
};

export default function HomePageHeroClient({
  lang,
  sliderData,
  readMoreText,
}: HomePageHeroClientProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [direction, setDirection] = useState(1); // Track slide direction
  const slideIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const nextSlide = useCallback(() => {
    if (sliderData.length > 0) {
      setDirection(1);
      setCurrentSlide((prev) => (prev + 1) % sliderData.length);
    }
  }, [sliderData.length]);

  const prevSlide = useCallback(() => {
    if (sliderData.length > 0) {
      setDirection(-1);
      setCurrentSlide(
        (prev) => (prev - 1 + sliderData.length) % sliderData.length
      );
    }
  }, [sliderData.length]);

  const goToSlide = useCallback(
    (index: number) => {
      if (index === currentSlide || sliderData.length === 0) return;

      setDirection(index > currentSlide ? 1 : -1);
      setCurrentSlide(index);
    },
    [currentSlide, sliderData.length]
  );

  // Clear interval on manual navigation and restart
  const handleManualNavigation = useCallback(
    (navigationFn: () => void) => {
      if (slideIntervalRef.current) {
        clearInterval(slideIntervalRef.current);
      }
      navigationFn();

      // Restart auto-advance after manual navigation
      if (sliderData.length > 1) {
        slideIntervalRef.current = setInterval(nextSlide, 5000); // 5 seconds
      }
    },
    [nextSlide, sliderData.length]
  );

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (sliderData.length > 1) {
      slideIntervalRef.current = setInterval(nextSlide, 5000);
    }

    return () => {
      if (slideIntervalRef.current) clearInterval(slideIntervalRef.current);
    };
  }, [nextSlide, sliderData.length]);

  const slide = sliderData[currentSlide];

  // Additional safety check to ensure slide exists
  if (!slide) {
    return (
      <section className="relative aspect-[80/51] lg:aspect-auto lg:h-screen flex items-center justify-center overflow-hidden -mt-20 pt-20">
        <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
        <div className="relative z-10 text-center text-white">
          <div className="h-12 bg-gray-300 rounded w-96 mb-4 animate-pulse"></div>
          <div className="h-6 bg-gray-300 rounded w-32 animate-pulse"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative aspect-[80/51] lg:aspect-auto lg:h-screen flex items-center justify-center overflow-hidden lg:-mt-20 lg:pt-20">
      {/* Background Image Container - No gap overlapping transitions */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Base background image - always visible to prevent gaps */}
        <div className="absolute inset-0 w-full h-full">
          {slide?.images?.[0] && (
            <Image
              src={getImageUrl(slide?.images?.[0]?.url || "")}
              alt={slide?.title || "Hero slide"}
              fill
              className="object-cover"
              priority={true}
              quality={90}
              draggable={false}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>
        </div>

        {/* Sliding overlay images */}
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={`slide-${slide.id}`}
            custom={direction}
            initial={{ x: direction > 0 ? "100%" : "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: direction > 0 ? "-100%" : "100%" }}
            transition={{
              type: "tween",
              duration: 1.2,
              ease: [0.25, 0.1, 0.25, 1.0],
            }}
            className="absolute inset-0 w-full h-full z-10"
            style={{
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          >
            {slide?.images?.[0] && (
              <Image
                src={getImageUrl(slide?.images?.[0]?.url || "")}
                alt={slide.title}
                fill
                className="object-cover"
                priority={currentSlide === 0}
                quality={90}
                draggable={false}
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/70"></div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Buttons - Hidden on mobile */}
      {sliderData.length > 1 && (
        <>
          {/* Left Navigation Button */}
          <button
            onClick={() => handleManualNavigation(prevSlide)}
            className="hidden sm:absolute left-4 md:left-8 top-1/2 z-30 w-12 h-12 md:w-16 md:h-16 bg-white/15 backdrop-blur-md rounded-full sm:flex items-center justify-center transition-all duration-500 hover:bg-white/25 hover:scale-110 group border border-white/20 shadow-xl cursor-pointer"
            style={{
              transform: "translateY(-50%)",
              willChange: "transform",
            }}
            aria-label="Previous slide"
          >
            <ChevronLeft
              size={28}
              className="text-white transition-all duration-300 group-hover:-translate-x-1"
            />
          </button>

          {/* Right Navigation Button */}
          <button
            onClick={() => handleManualNavigation(nextSlide)}
            className="hidden sm:absolute right-4 md:right-8 top-1/2 z-30 w-12 h-12 md:w-16 md:h-16 bg-white/15 backdrop-blur-md rounded-full sm:flex items-center justify-center transition-all duration-500 hover:bg-white/25 hover:scale-110 group border border-white/20 shadow-xl cursor-pointer"
            style={{
              transform: "translateY(-50%)",
              willChange: "transform",
            }}
            aria-label="Next slide"
          >
            <ChevronRight
              size={28}
              className="text-white transition-all duration-300 group-hover:translate-x-1"
            />
          </button>
        </>
      )}

      {/* Content Container */}
      <div className="relative z-10 text-center text-white w-full px-4">
        <div className="min-h-[120px] sm:min-h-[200px] flex flex-col items-center justify-center space-y-4 sm:space-y-8 max-w-7xl mx-auto">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={`content-${slide.id}`}
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                opacity: {
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1.0],
                },
                y: {
                  duration: 0.9,
                  ease: [0.25, 0.1, 0.25, 1.0],
                },
                scale: {
                  duration: 0.8,
                  ease: [0.25, 0.1, 0.25, 1.0],
                },
                delay: 0.3,
              }}
              className="absolute inset-0 flex flex-col items-center justify-center space-y-4 sm:space-y-8"
            >
              {/* Main Title */}
              <div className="min-h-[50px] sm:min-h-[80px] lg:min-h-[120px] flex items-center justify-center w-full">
                <h1
                  className="text-white text-center uppercase font-semibold text-[18px] sm:text-[28px] lg:text-[44px] font-inter leading-tight sm:leading-normal w-full max-w-6xl px-4 line-clamp-2 sm:line-clamp-3"
                  style={{
                    textShadow:
                      "2px 2px 8px rgba(0,0,0,0.7), 0 0 20px rgba(0,0,0,0.3)",
                    letterSpacing: "0.02em",
                  }}
                >
                  {slide.title}
                </h1>
              </div>

              {/* CTA Button */}
              <div className="flex justify-center w-full">
                <Link
                  href={`/${lang}/projects/${slide.slug}`}
                  className="group inline-flex items-center gap-[8px] sm:gap-[10px] px-[16px] sm:px-[24px] py-[12px] sm:py-[17px] rounded-[20px] sm:rounded-[30px] bg-[#1A5852] text-white hover:bg-white hover:text-[#1A5852] transition-all duration-300 shadow-lg cursor-pointer"
                >
                  <ChevronRight
                    size={14}
                    className="sm:w-[18px] sm:h-[18px] transition-colors duration-300"
                    color="currentColor"
                  />
                  <span className="font-inter text-[12px] sm:text-[16px] font-medium leading-[12px] sm:leading-[16px] capitalize">
                    {readMoreText || "Read More"}
                  </span>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 sm:bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2 sm:gap-3">
        {sliderData.map((_, index) => (
          <motion.button
            key={index}
            onClick={() => handleManualNavigation(() => goToSlide(index))}
            className={`transition-all duration-500 ease-out bg-white ${
              index === currentSlide
                ? "w-4 h-2 sm:w-6 sm:h-3" // Active: 16px x 8px on mobile, 24px x 12px on desktop
                : "w-2 h-2 sm:w-3 sm:h-3 opacity-50" // Inactive: 8px x 8px on mobile, 12px x 12px on desktop
            }`}
            style={{
              borderRadius: "10px",
            }}
            whileHover={{
              scale: 1.1,
              transition: { duration: 0.2, ease: [0.23, 1, 0.32, 1] },
            }}
            whileTap={{
              scale: 0.95,
              transition: { duration: 0.1 },
            }}
            animate={{
              width: index === currentSlide ? "16px" : "8px",
              height: index === currentSlide ? "8px" : "8px",
              opacity: index === currentSlide ? 1 : 0.5,
            }}
            transition={{
              width: { duration: 0.4, ease: [0.23, 1, 0.32, 1] },
              height: { duration: 0.4, ease: [0.23, 1, 0.32, 1] },
              opacity: { duration: 0.3 },
            }}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
